// Navegação mobile (md:hidden): uma bottom bar fixa com os destinos mais usados +
// um botão "☰" que abre um drawer inferior com o menu completo. Substitui a sidebar,
// que no mobile viraria um Sheet sem gatilho acessível.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useRouterState, type LinkProps } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
  Building2,
  ChevronRight,
  ChevronsUpDown,
  Cookie,
  Download,
  LogIn,
  LogOut,
  Menu,
  Moon,
  Sun,
  UserRound,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logout, useActiveOrganization, useSession } from "@/lib/auth-client";
import { usePreferences } from "./preferences-store.js";
import { LanguageSubmenu } from "./language.js";
import { AppBrandMark } from "./brand.js";
import { BrandDialog } from "./brand-dialog.js";
import { useInstallPrompt } from "./hooks/use-install-prompt.js";
import {
  AUDITORIA,
  BOTTOM_BAR_LINKS,
  CONFIGURACOES,
  DASHBOARD_LINK,
  DISCADOR_LINK,
  clickToCallNavGroup,
  filterNavGroup,
  FILA,
  FINANCEIRO,
  GESTAO,
  INTEGRACOES,
  JORNADA_LINK,
  manualDialNavLink,
  MEU_DESEMPENHO_LINK,
  MONITORAMENTO,
  navTranslationKey,
  RELATORIOS,
  showInNav,
  SUPORTE,
  type NavGroupDef,
  type NavLink,
} from "./nav-config.js";
import { canAccess } from "./lib/access.js";
import { useJourneyProgress } from "./hooks/use-journey-progress.js";
import { useClickToCallEnabled } from "./lib/click-to-call-enabled.js";
import { useManualDialEnabled } from "./lib/manual-dial-enabled.js";
import { useAccessSnapshot } from "./hooks/use-viewer-role.js";
import { useSupportEnabled } from "./lib/support-enabled.js";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { openConsentPreferences } from "./lib/consent.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function useTranslatedNavLabel(label: string): string {
  const { t } = useTranslation();
  const key = navTranslationKey(label);
  return key ? t(key) : label;
}

// Uma rota está ativa quando é a raiz (match exato) ou prefixo do pathname atual.
function isActive(to: LinkProps["to"], pathname: string): boolean {
  const path = String(to);
  return path === "/" ? pathname === "/" : pathname.startsWith(path);
}

// Item da bottom bar: só o ícone, dentro de uma pílula que acende no ativo.
//
// O rótulo continua no DOM como `sr-only`: sem ele, um ícone solto não tem
// nome acessível nenhum para leitor de tela — e um `aria-label` no lugar
// diria a mesma coisa sem servir de alvo para os testes.
function BottomBarItem({ link, pathname }: { link: NavLink; pathname: string }) {
  const label = useTranslatedNavLabel(link.label);
  const active = isActive(link.to, pathname);
  const Icon = link.icon;
  return (
    <Link
      to={link.to}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex size-13 items-center justify-center rounded-full transition-colors",
        active
          ? "bg-primary/12 text-primary dark:bg-primary/20"
          : "text-muted-foreground active:bg-muted",
      )}
    >
      <Icon className="size-6 shrink-0" />
      <span className="sr-only">{label}</span>
    </Link>
  );
}

// Linha de link dentro do drawer. Fecha o drawer ao navegar (DrawerClose).
function DrawerLinkRow({
  to,
  label,
  icon: Icon,
  pathname,
  badge,
  highlight = false,
  active: activeOverride,
}: {
  to: LinkProps["to"];
  label: string;
  icon?: LucideIcon;
  pathname: string;
  // Contador do tipo "1/7" à direita do rótulo (hoje só a jornada usa).
  badge?: string;
  // Item em destaque mesmo fora da rota ativa — ver a jornada no drawer.
  highlight?: boolean;
  // Override explícito do "ativo" (mesmo padrão de NavLinkItem na sidebar).
  // Só precisa disso quem tem subrota irmã disputando o mesmo prefixo — ex.:
  // Ajuda x Meus chamados, onde o startsWith padrão acenderia os dois.
  active?: boolean;
}) {
  const translatedLabel = useTranslatedNavLabel(label);
  const { t } = useTranslation();
  const active = activeOverride ?? isActive(to, pathname);
  return (
    <DrawerClose
      nativeButton={false}
      role="link"
      render={
        <Link
          to={to}
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
            active || highlight
              ? "bg-primary/10 font-medium text-primary"
              : "text-foreground hover:bg-muted",
            highlight && !active && "ring-1 ring-primary/25",
          )}
        />
      }
    >
      {Icon ? (
        <Icon className="size-4 shrink-0" />
      ) : (
        <span className="size-1.5 shrink-0 rounded-full bg-current opacity-40" />
      )}
      <span className="truncate">{translatedLabel}</span>
      {badge ? (
        // `aria-label` num `<span>` sem role é IGNORADO pela ARIA (elemento
        // `generic`) — padrão que funciona: dígito com `aria-hidden`, mais
        // um `sr-only` com o texto por extenso ao lado.
        <span className="ml-auto shrink-0 rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none tabular-nums text-primary-foreground">
          <span aria-hidden="true">{badge}</span>
          <span className="sr-only">
            {t("navigation.startHere")}: {badge}
          </span>
        </span>
      ) : null}
    </DrawerClose>
  );
}

// O match exato das telas com subrotas evita acender mais de um item,
// como Ligações e Números inválidos.
function isActiveNoDrawerGroup(to: LinkProps["to"], pathname: string): boolean {
  return to === "/support/help" || to === "/reports/calls" || to === "/reports"
    ? pathname === to
    : isActive(to, pathname);
}

// Seção colapsável do drawer: cabeçalho (ícone + label) que abre/fecha os itens.
// Começa aberta só quando contém a rota ativa, pra economizar espaço e evitar
// que o usuário role até o fim atrás do menu que ele quer.
function DrawerGroup({ group, pathname }: { group: NavGroupDef; pathname: string }) {
  const groupLabel = useTranslatedNavLabel(group.label);
  const GroupIcon = group.icon;
  const hasActive = group.items.some((item) => isActiveNoDrawerGroup(item.to, pathname));
  return (
    <Collapsible defaultOpen={hasActive} className="group/dg">
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-muted">
        <GroupIcon className="size-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 text-left font-medium">{groupLabel}</span>
        <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[open]/dg:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent className="pl-4">
        {group.items.map((item) => (
          <DrawerLinkRow
            key={String(item.to)}
            to={item.to}
            label={item.label}
            pathname={pathname}
            active={isActiveNoDrawerGroup(item.to, pathname)}
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// Rodapé do drawer: o bloco da conta é o GATILHO de um dropdown com perfil,
// marca, tema, instalação e sair — mesmo padrão do rodapé da sidebar no
// desktop (SidebarUser em app-sidebar.tsx).
//
// Antes esses itens ficavam todos abertos em lista aqui embaixo: no celular
// eles empurravam o menu de navegação para fora da tela, e o usuário precisava
// rolar o drawer inteiro para chegar em "Sair".
function DrawerAccount({
  onNavigate,
  onOpenBrands,
}: {
  onNavigate: () => void;
  onOpenBrands: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const user = session?.user;
  const { data: activeOrg } = useActiveOrganization();
  const theme = usePreferences((s) => s.theme);
  const setTheme = usePreferences((s) => s.setTheme);

  const primary = user ? user.name || user.email : t("common.unauthenticated");
  const secondary = user ? user.email : t("common.tapToSignIn");
  const initials = (user ? user.name || user.email || "?" : "—").slice(0, 2).toUpperCase();

  const activeOrgName = activeOrg?.name ?? null;

  async function handleSignOut() {
    await logout();
    onNavigate();
    void navigate({ to: "/auth" });
  }

  return (
    <div className="border-t border-border pt-2">
      {/* modal={false}: o Drawer LAI já bloqueia o fundo e trava o scroll do
          body — um segundo bloqueio do dropdown empilharia scroll-lock sobre
          scroll-lock no mesmo body, e quem desfaz por último manda. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-muted">
          <Avatar className="size-8 rounded-md">
            {user?.image ? <AvatarImage src={user.image} alt={`Avatar de ${primary}`} /> : null}
            <AvatarFallback className="rounded-md text-xs">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="truncate text-sm">{primary}</span>
            <span className="truncate text-xs text-muted-foreground">{secondary}</span>
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </DropdownMenuTrigger>
        {/* side="top": o gatilho fica no rodapé de um drawer que já nasce
            colado na base da tela — para baixo não sobra espaço. */}
        <DropdownMenuContent side="top" align="start" className="w-(--anchor-width) min-w-56">
          <DropdownMenuGroup>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col leading-tight">
                <span className="text-sm">{primary}</span>
                <span className="truncate text-xs text-muted-foreground">{secondary}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {user ? (
              <>
                {/* Fechar o dropdown não fecha o drawer que o contém: sem o
                  onNavigate, navegar deixaria o menu inteiro aberto por cima
                  da tela de destino. */}
                <DropdownMenuItem
                  render={<Link to="/profile" />}
                  nativeButton={false}
                  role="link"
                  onClick={onNavigate}
                >
                  <UserRound />
                  {t("common.profile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    // Impede o fechamento imediato do menu engolir a abertura do
                    // dialog (mesma razão do OrgSwitcherItem na sidebar).
                    e.preventDefault();
                    onOpenBrands();
                  }}
                >
                  <Building2 />
                  <span
                    className={`flex-1 truncate${activeOrgName ? "" : " text-muted-foreground"}`}
                  >
                    {activeOrgName ?? t("common.selectBrand")}
                  </span>
                  <ChevronRight className="ml-auto size-4 text-muted-foreground" />
                </DropdownMenuItem>
                {/* Alternância direta, não o submenu da sidebar: submenu no
                  celular exige acertar o item e esperar o voo do painel. */}
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    setTheme(theme === "dark" ? "light" : "dark");
                  }}
                >
                  {theme === "dark" ? <Moon /> : <Sun />}
                  {t("common.theme")}{" "}
                  {theme === "dark"
                    ? t("common.themes.dark").toLocaleLowerCase()
                    : t("common.themes.light").toLocaleLowerCase()}
                </DropdownMenuItem>
                <LanguageSubmenu />
                <DropdownMenuItem onClick={openConsentPreferences}>
                  <Cookie />
                  {t("cookieConsent.preferences")}
                </DropdownMenuItem>
                {/* "Configurações" saiu daqui: virou grupo próprio na lista acima
                  (CONFIGURACOES), com uma rota por assunto. */}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleSignOut()}>
                  <LogOut />
                  {t("common.signOut")}
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem
                render={<Link to="/auth" />}
                nativeButton={false}
                role="link"
                onClick={onNavigate}
              >
                <LogIn />
                {t("common.signIn")}
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function MobileBottomNav() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const access = useAccessSnapshot();
  // O grupo "Click to call" só aparece com o recurso habilitado na org; a tela
  // que o habilita fica sempre em Integrações.
  const clickToCallEnabled = useClickToCallEnabled(
    canAccess({ ...access, path: "/management/click-to-call-audit" }),
  );
  const clickToCall = clickToCallNavGroup(clickToCallEnabled);
  // "Discagem manual" (/phone) idem: só entra no drawer com o toggle da marca
  // ligado em Configurações › Discador — ver a mesma regra em app-sidebar.tsx.
  const manualDialEnabled = useManualDialEnabled();
  const discagemManual = showInNav(access, "/phone") ? manualDialNavLink(manualDialEnabled) : null;
  // "Meus chamados" só existe no drawer quando o back tem as envs do Jira
  // configuradas — ver a mesma regra em app-sidebar.tsx.
  const supportEnabled = useSupportEnabled();
  const [menuOpen, setMenuOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  // Já rodando instalado não há o que instalar — mesma regra do convite em
  // /profile, que também some nesse caso.
  const { isStandalone } = useInstallPrompt();
  // Espelha a sidebar: com passo acionável pendente, a jornada sai do fim do
  // drawer e vira o primeiro item, destacada e com o contador.
  const journey = useJourneyProgress();
  const jornadaPendente = !!journey?.pendente;
  // Grupo Suporte: com o suporte desligado (sem envs do Jira no back), some
  // só "Meus chamados" — a Ajuda (FAQ + assistente) existe sem Jira.
  const grupoSuporte = {
    ...SUPORTE,
    items: supportEnabled
      ? SUPORTE.items
      : SUPORTE.items.filter((i) => i.to !== "/support/tickets"),
  };
  // Espelha a sidebar: o vendedor não vê a jornada.
  const jornadaRow = showInNav(access, JORNADA_LINK.to as string) ? (
    <DrawerLinkRow
      to={JORNADA_LINK.to}
      label={JORNADA_LINK.label}
      icon={JORNADA_LINK.icon}
      pathname={pathname}
      badge={jornadaPendente && journey ? `${journey.done}/${journey.total}` : undefined}
      highlight={jornadaPendente}
    />
  ) : null;

  return (
    /* Pílula flutuante em vidro fosco, no lugar da faixa colada na borda:
       centralizada, sem encostar nos cantos, com o conteúdo da tela passando
       por baixo e desfocado. A altura reservada para ela sai de
       --mobile-nav-space (index.css) — mexer aqui pede mexer lá. */
    <nav
      aria-label={t("common.mainNavigation")}
      className="fixed bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] left-1/2 z-40 flex -translate-x-1/2 items-center gap-1 rounded-full border border-border/60 bg-background/70 px-2 py-1.5 shadow-lg shadow-black/5 backdrop-blur-xl supports-backdrop-filter:bg-background/60 md:hidden dark:border-white/10 dark:bg-background/60"
    >
      {BOTTOM_BAR_LINKS.filter((link) => canAccess({ ...access, path: link.to as string })).map(
        (link) => (
          <BottomBarItem key={String(link.to)} link={link} pathname={pathname} />
        ),
      )}

      <Drawer open={menuOpen} onOpenChange={setMenuOpen}>
        <DrawerTrigger
          className={cn(
            "flex size-13 items-center justify-center rounded-full transition-colors",
            menuOpen
              ? "bg-primary/12 text-primary dark:bg-primary/20"
              : "text-muted-foreground active:bg-muted",
          )}
        >
          <Menu className="size-6 shrink-0" />
          <span className="sr-only">{t("common.menu")}</span>
        </DrawerTrigger>
        <DrawerContent className="max-h-[85vh]">
          <DrawerHeader className="pb-1 text-left">
            <DrawerTitle render={<div />}>
              <AppBrandMark />
            </DrawerTitle>
          </DrawerHeader>

          <div className="overflow-y-auto px-2 pb-4">
            <div className="px-3 pt-1 pb-1 text-[11px] font-semibold tracking-wide text-muted-foreground/70 uppercase">
              {t("common.operation")}
            </div>
            {jornadaPendente && jornadaRow}
            {showInNav(access, DASHBOARD_LINK.to as string) && (
              <DrawerLinkRow
                to={DASHBOARD_LINK.to}
                label={DASHBOARD_LINK.label}
                icon={DASHBOARD_LINK.icon}
                pathname={pathname}
              />
            )}
            {canAccess({ ...access, path: DISCADOR_LINK.to as string }) && (
              <DrawerLinkRow
                to={DISCADOR_LINK.to}
                label={DISCADOR_LINK.label}
                icon={DISCADOR_LINK.icon}
                pathname={pathname}
              />
            )}
            {showInNav(access, MEU_DESEMPENHO_LINK.to as string) && (
              <DrawerLinkRow
                to={MEU_DESEMPENHO_LINK.to}
                label={MEU_DESEMPENHO_LINK.label}
                icon={MEU_DESEMPENHO_LINK.icon}
                pathname={pathname}
              />
            )}
            {discagemManual && (
              <DrawerLinkRow
                to={discagemManual.to}
                label={discagemManual.label}
                icon={discagemManual.icon}
                pathname={pathname}
              />
            )}
            {[
              MONITORAMENTO,
              RELATORIOS,
              AUDITORIA,
              GESTAO,
              FILA,
              FINANCEIRO,
              ...(clickToCall ? [clickToCall] : []),
              INTEGRACOES,
              CONFIGURACOES,
              grupoSuporte,
            ].map((g) => {
              const fg = filterNavGroup(g, access);
              return fg ? <DrawerGroup key={fg.label} group={fg} pathname={pathname} /> : null;
            })}
            {!jornadaPendente && jornadaRow}
            {/* Último item da lista, logo acima da conta: instalar é algo que
                se faz uma vez, então não disputa espaço com os destinos do
                dia a dia — mas continua na lista, não escondido dentro do
                menu do perfil. */}
            {!isStandalone && (
              <DrawerLinkRow
                to="/install"
                label="Instalar app"
                icon={Download}
                pathname={pathname}
              />
            )}

            <div className="mt-3">
              <DrawerAccount
                onNavigate={() => setMenuOpen(false)}
                onOpenBrands={() => {
                  // Fecha o drawer e abre o dialog (renderizado fora do drawer, senão
                  // desmontaria junto). Mesma dinâmica da sidebar no desktop.
                  setMenuOpen(false);
                  setBrandsOpen(true);
                }}
              />
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      <BrandDialog open={brandsOpen} onOpenChange={setBrandsOpen} />
      {/* Fora do Drawer pelo mesmo motivo do BrandDialog: dentro dele, o
          dialog desmontaria junto quando o drawer fecha. */}
    </nav>
  );
}
